import PropTypes from 'prop-types';
import { useForm } from 'react-final-form';
import { useEffect, useRef, useState } from 'react';
import { Autocomplete, CircularProgress, ListItem, ListItemText, Typography } from '@mui/material';

import TextField from './TextField';
import CatalogStatusChip from './CatalogStatusChip';
import { validateMinLength } from './formValidations';

// `disabled` marks an option the reviewer genuinely cannot pick (no NDC), so it recolours the whole
// line. A catalog product disabled for ordering is still pickable, so it must not — it carries a
// chip instead, which reads at a glance without lengthening the Product ID line (POM-6306).
const optionIdColor = (option) => (option?.disabled ? 'error.main' : 'text.secondary');

const AutoSuggest = ({
  id,
  name,
  label,
  autoFocus,
  loading,
  options,
  fetchOptions,
  handleSelect,
  isPrescriptionReview,
}) => {
  const form = useForm();
  const [inputValue, setInputValue] = useState(form.getState().values[name] || '');
  // The last trimmed term we actually searched, so a no-op change (e.g. adding a trailing space)
  // doesn't fire a redundant identical request. A ref — it must not trigger a re-render.
  const lastSearchedRef = useRef('');

  // Mount-only: if the field is prefilled (a consumer seeds a name before opening), run the search
  // once on open so its results are ready — preserving the old inputValue-effect's on-mount fetch
  // without also firing on select/typing. Empty-start consumers skip this.
  useEffect(() => {
    const initial = inputValue.trim();
    if (initial.length >= 3) {
      lastSearchedRef.current = initial;
      fetchOptions(initial);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onInputChange = (_, value, reason) => {
    // MUI passes a string here, but guard against null/undefined so a .trim() can never break the
    // field if the value is ever absent.
    const nextValue = value ?? '';
    setInputValue(nextValue);
    // Only react to actual typing. MUI also fires onInputChange with reason 'reset' when an option
    // is picked (it sets the input to the option's label) — reacting there would kick off a
    // redundant search for the selected label (which also briefly re-disabled Confirm) and would
    // clear the fresh selection. So we drive search + selection-invalidation from typing alone.
    if (reason !== 'input') return;
    // A keystroke changes the search: invalidate any prior selection, then (re)fetch once the term
    // is long enough. Replaces the old inputValue-watching effect that also fired on select.
    handleSelect('');
    // Trim what we search on so leading/trailing spaces aren't sent in the payload (and don't count
    // toward the 3-char minimum). The raw text stays in the box so the user's typing isn't disturbed.
    const trimmed = nextValue.trim();
    if (trimmed.length < 3) {
      // Reset so the same term can be searched again after clearing back below the minimum.
      lastSearchedRef.current = '';
      return;
    }
    // Skip when the trimmed term is unchanged (e.g. a trailing space was added/removed) — that would
    // otherwise fire a redundant identical request.
    if (trimmed === lastSearchedRef.current) return;
    lastSearchedRef.current = trimmed;
    fetchOptions(trimmed);
  };

  const onChange = (_, value) => {
    handleSelect(value?.id);
    form.batch(() => form.change(name, value));
  };

  const validateSelectionRequired = (value) => {
    if (isPrescriptionReview && (!value || !value.id)) {
      return 'Please select a drug from the list';
    }
    return undefined;
  };

  return (
    <Autocomplete
      freeSolo
      id={`${id}-autosuggest`}
      options={options}
      getOptionLabel={(option) => option?.label || inputValue}
      renderOption={(props, option) => (
        // eslint-disable-next-line react/jsx-props-no-spreading
        <ListItem {...props} key={option?.id}>
          <ListItemText
            primary={
              option.displayName ? (
                <Typography>{option.displayName}</Typography>
              ) : (
                <>
                  <Typography>Drug - {option.drugName}</Typography>
                  <Typography>Strength - {option.strength}</Typography>
                </>
              )
            }
          />
          {option?.productId && (
            <Typography
              variant='body2'
              color={optionIdColor(option)}
              sx={{ ml: 2, whiteSpace: 'nowrap', alignSelf: 'center' }}
            >
              Product ID: {option.productId}
              {option?.disabled ? ' — No NDC' : ''}
            </Typography>
          )}
          {option?.isCatalogDisabled && <CatalogStatusChip />}
        </ListItem>
      )}
      filterOptions={(drugs) => drugs}
      getOptionDisabled={(option) => !!option?.disabled}
      disableClearable
      value={inputValue || ''}
      onInputChange={onInputChange}
      onChange={onChange}
      renderInput={(params) => {
        return (
          <TextField
            name={name}
            id={id}
            label={label}
            required
            params={params}
            endAdornment={loading ? <CircularProgress size={20} /> : undefined}
            autoFocus={autoFocus}
            validations={[
              validateMinLength('Minimum of 3 characters', 3),
              validateSelectionRequired,
            ]}
          />
        );
      }}
    />
  );
};

AutoSuggest.propTypes = {
  name: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  autoFocus: PropTypes.bool,
  loading: PropTypes.bool,
  options: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.any)),
  fetchOptions: PropTypes.func.isRequired,
  handleSelect: PropTypes.func.isRequired,
  isPrescriptionReview: PropTypes.bool,
};

AutoSuggest.defaultProps = {
  autoFocus: false,
  loading: false,
  options: [],
  isPrescriptionReview: false,
};

export default AutoSuggest;
